import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
    Video, 
    Cloud, 
    CloudOff, 
    Calendar, 
    Award, 
    Target, 
    BarChart3, 
    Loader2, 
    CheckCircle, 
    AlertTriangle,
    RefreshCw,
    Save,
    Mic,
    FileText,
    Settings,
    TrendingUp,
    Download,
    Upload,
    Terminal,
    Eye,
    EyeOff,
    Pencil
} from 'lucide-react';

const difficultyLevels = [
    { value: 'easy', label: 'Easy', description: 'Forgiving evaluation', color: 'bg-emerald-500' },
    { value: 'medium', label: 'Medium', description: 'Standard expectations', color: 'bg-amber-500' },
    { value: 'difficult', label: 'Difficult', description: 'Strict evaluation criteria', color: 'bg-red-500' }
];

const EvolveSettings = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    
    // EVOLVE Config State
    const [evolveConfig, setEvolveConfig] = useState({
        enabled: true,
        sessions_required_per_month: 1,
        pass_score_threshold: 75,
        evaluation_difficulty: 'difficult',
        max_session_duration_minutes: 25,
        auto_transcription_enabled: true,
        qa_questions_count: 3,
        categories_weights: {
            content_clarity: 15,
            structure_flow: 15,
            verbal_communication: 15,
            qa_handling: 15,
            confidence_presence: 15,
            slide_effectiveness: 10,
            engagement_level: 15
        }
    });
    
    // OneDrive Config State
    const [onedriveConfig, setOnedriveConfig] = useState({
        enabled: false,
        auth_method: 'ropc',
        username: '',
        password: '',
        client_id: '',
        client_secret: '',
        tenant_id: '',
        target_user_email: ''
    });
    
    // System Health State
    const [systemHealth, setSystemHealth] = useState(null);
    
    // Secret visibility states
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [editingClientSecret, setEditingClientSecret] = useState(false);
    const [editingPassword, setEditingPassword] = useState(false);
    const [healthLoading, setHealthLoading] = useState(false);
    const [installingLibreOffice, setInstallingLibreOffice] = useState(false);
    const [libreOfficeStatus, setLibreOfficeStatus] = useState(null);
    const [installingFFmpeg, setInstallingFFmpeg] = useState(false);
    const [ffmpegStatus, setFfmpegStatus] = useState(null);

    const fetchEvolveSettings = useCallback(async () => {
        try {
            setLoading(true);
            
            // Fetch EVOLVE config
            const [configRes, onedriveRes] = await Promise.all([
                api.get('/evolve/admin/config').catch(() => ({ data: null })),
                api.get('/evolve/admin/onedrive-config').catch(() => ({ data: null }))
            ]);
            
            if (configRes.data) {
                setEvolveConfig(prev => ({ ...prev, ...configRes.data }));
            }
            if (onedriveRes.data) {
                setOnedriveConfig(prev => ({ ...prev, ...onedriveRes.data }));
            }
            
        } catch (error) {
            console.error('Failed to fetch EVOLVE settings:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    const checkSystemHealth = useCallback(async () => {
        setHealthLoading(true);
        try {
            const response = await api.get('/evolve/admin/system/health');
            setSystemHealth(response.data);
        } catch (error) {
            console.error('Failed to check system health:', error);
        } finally {
            setHealthLoading(false);
        }
    }, [api]);

    const checkLibreOfficeStatus = useCallback(async () => {
        try {
            const response = await api.get('/evolve/admin/system/libreoffice-status');
            setLibreOfficeStatus(response.data);
        } catch (error) {
            console.error('Failed to check LibreOffice status:', error);
            setLibreOfficeStatus({ installed: false, error: error.message });
        }
    }, [api]);

    const checkFfmpegStatus = useCallback(async () => {
        try {
            const response = await api.get('/evolve/admin/system/ffmpeg-status');
            setFfmpegStatus(response.data);
        } catch (error) {
            console.error('Failed to check FFmpeg status:', error);
            setFfmpegStatus({ installed: false, error: error.message });
        }
    }, [api]);

    // Fetch settings on component mount
    useEffect(() => {
        fetchEvolveSettings();
        checkSystemHealth();
        checkLibreOfficeStatus();
        checkFfmpegStatus();
    }, [fetchEvolveSettings, checkSystemHealth, checkLibreOfficeStatus, checkFfmpegStatus]);

    const installLibreOffice = async () => {
        setInstallingLibreOffice(true);
        try {
            const response = await api.post('/evolve/admin/system/install-libreoffice');
            if (response.data.success) {
                toast.success('LibreOffice installation started. This may take a few minutes.');
                // Poll for completion
                const checkInterval = setInterval(async () => {
                    const statusRes = await api.get('/evolve/admin/system/libreoffice-status');
                    setLibreOfficeStatus(statusRes.data);
                    if (statusRes.data.installed) {
                        clearInterval(checkInterval);
                        setInstallingLibreOffice(false);
                        toast.success('LibreOffice installed successfully!');
                        checkSystemHealth();
                    }
                }, 5000);
                // Timeout after 5 minutes
                setTimeout(() => {
                    clearInterval(checkInterval);
                    setInstallingLibreOffice(false);
                    checkLibreOfficeStatus();
                }, 300000);
            } else {
                toast.error('Failed to start LibreOffice installation: ' + (response.data.error || 'Unknown error'));
                setInstallingLibreOffice(false);
            }
        } catch (error) {
            toast.error('Failed to install LibreOffice: ' + (error.response?.data?.detail || error.message));
            setInstallingLibreOffice(false);
        }
    };

    const installFFmpeg = async () => {
        setInstallingFFmpeg(true);
        try {
            const response = await api.post('/evolve/admin/system/install-ffmpeg');
            if (response.data.success) {
                if (response.data.already_installed) {
                    toast.success('FFmpeg is already installed!');
                    setInstallingFFmpeg(false);
                    checkFfmpegStatus();
                } else {
                    toast.success('FFmpeg installation started. This usually takes about 1 minute.');
                    // Poll for completion
                    const checkInterval = setInterval(async () => {
                        const statusRes = await api.get('/evolve/admin/system/ffmpeg-status');
                        setFfmpegStatus(statusRes.data);
                        if (statusRes.data.installed) {
                            clearInterval(checkInterval);
                            setInstallingFFmpeg(false);
                            toast.success('FFmpeg installed successfully! Voice features are now ready.');
                            checkSystemHealth();
                        }
                    }, 3000);
                    // Timeout after 2 minutes
                    setTimeout(() => {
                        clearInterval(checkInterval);
                        setInstallingFFmpeg(false);
                        checkFfmpegStatus();
                    }, 120000);
                }
            } else {
                toast.error('Failed to start FFmpeg installation: ' + (response.data.error || 'Unknown error'));
                setInstallingFFmpeg(false);
            }
        } catch (error) {
            toast.error('Failed to install FFmpeg: ' + (error.response?.data?.detail || error.message));
            setInstallingFFmpeg(false);
        }
    };

    const saveConfig = async () => {
        setSaving(true);
        try {
            await api.put('/evolve/admin/config', evolveConfig);
            toast.success('EVOLVE settings saved successfully');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const saveOnedriveConfig = async () => {
        setSaving(true);
        try {
            await api.post('/evolve/admin/onedrive-config', onedriveConfig);
            toast.success('OneDrive settings saved');
        } catch (error) {
            console.error('OneDrive save error:', error);
            toast.error('Failed to save OneDrive settings: ' + (error.response?.data?.detail || error.message));
        } finally {
            setSaving(false);
        }
    };

    const testOneDriveConnection = async () => {
        setTesting(true);
        try {
            const response = await api.post('/evolve/admin/onedrive-config/test');
            if (response.data.success) {
                toast.success('OneDrive connection successful!');
            } else {
                toast.error('OneDrive connection failed: ' + (response.data.error || 'Unknown error'));
            }
        } catch (error) {
            toast.error('OneDrive connection test failed');
        } finally {
            setTesting(false);
        }
    };

    const exportOnedriveConfig = async () => {
        try {
            const response = await api.get('/evolve/admin/onedrive-config/export');
            const dataStr = JSON.stringify(response.data, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `onedrive-config-${new Date().toISOString().slice(0,10)}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('OneDrive config exported! Save this file to import on production.');
        } catch (error) {
            toast.error('Failed to export OneDrive config');
        }
    };

    const importOnedriveConfig = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        try {
            const text = await file.text();
            const data = JSON.parse(text);
            
            if (data.export_type !== 'onedrive_config' || !data.config) {
                toast.error('Invalid OneDrive config file');
                return;
            }
            
            await api.post('/evolve/admin/onedrive-config/import', data);
            toast.success('OneDrive config imported successfully!');
            
            // Refresh the config
            const onedriveRes = await api.get('/evolve/admin/onedrive-config');
            if (onedriveRes.data) {
                setOnedriveConfig(prev => ({ ...prev, ...onedriveRes.data }));
            }
        } catch (error) {
            toast.error('Failed to import OneDrive config: ' + (error.message || 'Invalid file'));
        }
        
        // Reset file input
        event.target.value = '';
    };

    const updateConfig = (key, value) => {
        setEvolveConfig(prev => ({ ...prev, [key]: value }));
    };

    const updateCategoryWeight = (category, value) => {
        setEvolveConfig(prev => ({
            ...prev,
            categories_weights: {
                ...prev.categories_weights,
                [category]: value
            }
        }));
    };

    const totalWeight = Object.values(evolveConfig.categories_weights || {}).reduce((a, b) => a + b, 0);

    if (loading) {
        return (
            <Card className="lg:col-span-2 border-2 border-violet-500/20">
                <CardContent className="p-8 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="lg:col-span-2 border-2 border-violet-500/20">
            <CardHeader className="bg-gradient-to-r from-violet-500/5 to-pink-500/5">
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Video className="h-5 w-5 text-violet-500" />
                            EVOLVE Settings
                        </CardTitle>
                        <CardDescription>
                            Configure presentation coaching, compliance requirements, and evaluation criteria
                        </CardDescription>
                    </div>
                    <Button onClick={saveConfig} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-8 pt-6">
                
                {/* System Health */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold flex items-center gap-2">
                            <Settings className="h-4 w-4 text-violet-500" />
                            System Status
                        </Label>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={checkSystemHealth}
                            disabled={healthLoading}
                        >
                            {healthLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        </Button>
                    </div>
                    
                    {systemHealth && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="p-3 rounded-lg bg-background border text-center">
                                <Badge className={systemHealth.status === 'healthy' ? 'bg-emerald-500' : 'bg-amber-500'}>
                                    {systemHealth.status === 'healthy' ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertTriangle className="h-3 w-3 mr-1" />}
                                    {systemHealth.status}
                                </Badge>
                            </div>
                            <div className="p-3 rounded-lg bg-background border text-center">
                                <p className="text-xs text-muted-foreground">Conversion</p>
                                <p className="font-medium text-sm">{systemHealth.conversion_method || 'N/A'}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-background border text-center">
                                <p className="text-xs text-muted-foreground">OneDrive</p>
                                <p className="font-medium text-sm">{systemHealth.storage_info?.onedrive_enabled ? 'Connected' : 'Not configured'}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-background border text-center">
                                <p className="text-xs text-muted-foreground">LLM</p>
                                <p className="font-medium text-sm">{systemHealth.checks?.llm_key_available ? 'Available' : 'Missing'}</p>
                            </div>
                        </div>
                    )}
                </div>

                <Separator />

                {/* Compliance Settings */}
                <div className="space-y-4">
                    <Label className="text-base font-semibold flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-violet-500" />
                        Compliance Requirements
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        Set mandatory training requirements for all users
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-4 rounded-xl border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Sessions Required Per Month</Label>
                                <Badge variant="outline" className="text-violet-600">{evolveConfig.sessions_required_per_month}</Badge>
                            </div>
                            <Slider
                                value={[evolveConfig.sessions_required_per_month]}
                                onValueChange={([value]) => updateConfig('sessions_required_per_month', value)}
                                min={1}
                                max={10}
                                step={1}
                                className="[&>span]:bg-violet-500"
                            />
                            <p className="text-xs text-muted-foreground">
                                Users must complete this many sessions each month
                            </p>
                        </div>

                        <div className="p-4 rounded-xl border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Pass Score Threshold</Label>
                                <Badge variant="outline" className="text-emerald-600">{evolveConfig.pass_score_threshold}%</Badge>
                            </div>
                            <Slider
                                value={[evolveConfig.pass_score_threshold]}
                                onValueChange={([value]) => updateConfig('pass_score_threshold', value)}
                                min={50}
                                max={95}
                                step={5}
                                className="[&>span]:bg-emerald-500"
                            />
                            <p className="text-xs text-muted-foreground">
                                Minimum score to pass a session (recommended: 75%)
                            </p>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Evaluation Difficulty */}
                <div className="space-y-4">
                    <Label className="text-base font-semibold flex items-center gap-2">
                        <Target className="h-4 w-4 text-violet-500" />
                        Evaluation Difficulty
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        Set how strictly the AI evaluates presentations
                    </p>
                    
                    <div className="grid grid-cols-3 gap-3">
                        {difficultyLevels.map((level) => {
                            const isSelected = evolveConfig.evaluation_difficulty === level.value;
                            return (
                                <button
                                    key={level.value}
                                    onClick={() => updateConfig('evaluation_difficulty', level.value)}
                                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                                        isSelected 
                                            ? 'border-violet-500 bg-violet-500/5 ring-2 ring-violet-500/20' 
                                            : 'border-muted hover:border-violet-500/50'
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className={`w-3 h-3 rounded-full ${level.color}`} />
                                        <span className="font-semibold">{level.label}</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">{level.description}</p>
                                </button>
                            );
                        })}
                    </div>
                    
                    {evolveConfig.evaluation_difficulty === 'difficult' && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm">
                            <p className="text-red-700 dark:text-red-400 flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4" />
                                <strong>Difficult mode:</strong> Evaluations will be strict. Scores may be 10-15% lower than Medium mode.
                            </p>
                        </div>
                    )}
                </div>

                <Separator />

                {/* Session Settings */}
                <div className="space-y-4">
                    <Label className="text-base font-semibold flex items-center gap-2">
                        <Mic className="h-4 w-4 text-violet-500" />
                        Session Configuration
                    </Label>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="p-4 rounded-xl border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Max Session Duration</Label>
                                <Badge variant="outline">{evolveConfig.max_session_duration_minutes} min</Badge>
                            </div>
                            <Slider
                                value={[evolveConfig.max_session_duration_minutes]}
                                onValueChange={([value]) => updateConfig('max_session_duration_minutes', value)}
                                min={10}
                                max={60}
                                step={5}
                            />
                        </div>

                        <div className="p-4 rounded-xl border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Q&A Questions</Label>
                                <Badge variant="outline">{evolveConfig.qa_questions_count}</Badge>
                            </div>
                            <Slider
                                value={[evolveConfig.qa_questions_count]}
                                onValueChange={([value]) => updateConfig('qa_questions_count', value)}
                                min={1}
                                max={5}
                                step={1}
                            />
                        </div>

                        <div className="p-4 rounded-xl border flex items-center justify-between">
                            <div>
                                <Label className="text-sm font-medium">Auto Transcription</Label>
                                <p className="text-xs text-muted-foreground">Transcribe recordings with Whisper AI</p>
                            </div>
                            <Switch
                                checked={evolveConfig.auto_transcription_enabled}
                                onCheckedChange={(checked) => updateConfig('auto_transcription_enabled', checked)}
                            />
                        </div>

                        <div className="p-4 rounded-xl border flex items-center justify-between">
                            <div>
                                <Label className="text-sm font-medium">Feature Enabled</Label>
                                <p className="text-xs text-muted-foreground">Allow users to access EVOLVE</p>
                            </div>
                            <Switch
                                checked={evolveConfig.enabled}
                                onCheckedChange={(checked) => updateConfig('enabled', checked)}
                            />
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Category Weights */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-base font-semibold flex items-center gap-2">
                                <BarChart3 className="h-4 w-4 text-violet-500" />
                                Scoring Category Weights
                            </Label>
                            <p className="text-sm text-muted-foreground">
                                Adjust how much each category contributes to the overall score
                            </p>
                        </div>
                        <Badge variant={totalWeight === 100 ? "default" : "destructive"}>
                            Total: {totalWeight}%
                        </Badge>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-3">
                        {Object.entries(evolveConfig.categories_weights || {}).map(([category, weight]) => {
                            const label = category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            return (
                                <div key={category} className="p-3 rounded-lg border flex items-center gap-3">
                                    <div className="flex-1">
                                        <Label className="text-sm">{label}</Label>
                                    </div>
                                    <Slider
                                        value={[weight]}
                                        onValueChange={([value]) => updateCategoryWeight(category, value)}
                                        min={0}
                                        max={30}
                                        step={5}
                                        className="w-32"
                                    />
                                    <Badge variant="outline" className="w-12 justify-center">{weight}%</Badge>
                                </div>
                            );
                        })}
                    </div>
                    
                    {totalWeight !== 100 && (
                        <p className="text-sm text-red-500">
                            Warning: Weights should add up to 100% (currently {totalWeight}%)
                        </p>
                    )}
                </div>

                <Separator />

                {/* OneDrive Configuration */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-base font-semibold flex items-center gap-2">
                                {onedriveConfig.enabled ? <Cloud className="h-4 w-4 text-blue-500" /> : <CloudOff className="h-4 w-4 text-slate-400" />}
                                OneDrive Storage
                            </Label>
                            <p className="text-sm text-muted-foreground">
                                Store presentation files in Microsoft OneDrive
                            </p>
                        </div>
                        <Switch
                            checked={onedriveConfig.enabled}
                            onCheckedChange={(checked) => setOnedriveConfig(prev => ({ ...prev, enabled: checked }))}
                        />
                    </div>
                    
                    {onedriveConfig.enabled && (
                        <div className="p-4 rounded-xl border space-y-4">
                            {/* Authentication Method Selection */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Authentication Method</Label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="auth_method"
                                            value="ropc"
                                            checked={onedriveConfig.auth_method === 'ropc'}
                                            onChange={(e) => setOnedriveConfig(prev => ({ ...prev, auth_method: e.target.value }))}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm">Email & Password</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="auth_method"
                                            value="client_credentials"
                                            checked={onedriveConfig.auth_method === 'client_credentials'}
                                            onChange={(e) => setOnedriveConfig(prev => ({ ...prev, auth_method: e.target.value }))}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm">Azure AD App (Client Credentials)</span>
                                    </label>
                                </div>
                            </div>

                            {onedriveConfig.auth_method === 'ropc' ? (
                                /* Email & Password Authentication */
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm">Microsoft Account Email</Label>
                                        <Input
                                            type="email"
                                            value={onedriveConfig.username}
                                            onChange={(e) => setOnedriveConfig(prev => ({ ...prev, username: e.target.value }))}
                                            placeholder="user@domain.com"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm">Password</Label>
                                        <div className="flex gap-2">
                                            <div className="relative flex-1">
                                                <Input
                                                    type={showPassword ? "text" : "password"}
                                                    value={onedriveConfig.password}
                                                    onChange={(e) => setOnedriveConfig(prev => ({ ...prev, password: e.target.value }))}
                                                    placeholder={editingPassword ? "Enter new password" : (onedriveConfig.password ? "••••••••" : "Enter password")}
                                                    disabled={!editingPassword && onedriveConfig.password}
                                                />
                                                {onedriveConfig.password && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="absolute right-8 top-0 h-full px-2"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                    >
                                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </Button>
                                                )}
                                            </div>
                                            {onedriveConfig.password && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="icon"
                                                    onClick={() => {
                                                        setEditingPassword(!editingPassword);
                                                        if (!editingPassword) {
                                                            setOnedriveConfig(prev => ({ ...prev, password: '' }));
                                                        }
                                                    }}
                                                    title={editingPassword ? "Cancel edit" : "Edit password"}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-2 md:col-span-2">
                                        <Label className="text-sm">Target User Email (for file storage)</Label>
                                        <Input
                                            type="email"
                                            value={onedriveConfig.target_user_email}
                                            onChange={(e) => setOnedriveConfig(prev => ({ ...prev, target_user_email: e.target.value }))}
                                            placeholder="Same as account email or shared mailbox"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            This is where EVOLVE files will be stored. Usually the same as account email.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                /* Azure AD App Authentication */
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm">Client ID (App ID)</Label>
                                        <Input
                                            value={onedriveConfig.client_id}
                                            onChange={(e) => setOnedriveConfig(prev => ({ ...prev, client_id: e.target.value }))}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm">Client Secret</Label>
                                        <div className="flex gap-2">
                                            <div className="relative flex-1">
                                                <Input
                                                    type={showClientSecret ? "text" : "password"}
                                                    value={onedriveConfig.client_secret}
                                                    onChange={(e) => setOnedriveConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                                                    placeholder={editingClientSecret ? "Enter new client secret" : (onedriveConfig.client_secret ? "••••••••" : "Enter client secret")}
                                                    disabled={!editingClientSecret && onedriveConfig.client_secret}
                                                />
                                                {onedriveConfig.client_secret && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="absolute right-8 top-0 h-full px-2"
                                                        onClick={() => setShowClientSecret(!showClientSecret)}
                                                    >
                                                        {showClientSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </Button>
                                                )}
                                            </div>
                                            {onedriveConfig.client_secret && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="icon"
                                                    onClick={() => {
                                                        setEditingClientSecret(!editingClientSecret);
                                                        if (!editingClientSecret) {
                                                            setOnedriveConfig(prev => ({ ...prev, client_secret: '' }));
                                                        }
                                                    }}
                                                    title={editingClientSecret ? "Cancel edit" : "Edit client secret"}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm">Tenant ID</Label>
                                        <Input
                                            value={onedriveConfig.tenant_id}
                                            onChange={(e) => setOnedriveConfig(prev => ({ ...prev, tenant_id: e.target.value }))}
                                            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm">Target User Email</Label>
                                        <Input
                                            value={onedriveConfig.target_user_email}
                                            onChange={(e) => setOnedriveConfig(prev => ({ ...prev, target_user_email: e.target.value }))}
                                            placeholder="evolve@company.com"
                                        />
                                    </div>
                                </div>
                            )}
                            
                            <div className="flex items-center gap-3 flex-wrap">
                                <Button 
                                    variant="outline"
                                    onClick={testOneDriveConnection}
                                    disabled={testing}
                                >
                                    {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                                    Test Connection
                                </Button>
                                <Button onClick={saveOnedriveConfig} disabled={saving}>
                                    <Save className="h-4 w-4 mr-2" />
                                    Save OneDrive Config
                                </Button>
                                <div className="flex-1" />
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={exportOnedriveConfig}
                                    title="Export config to transfer to production"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Export
                                </Button>
                                <label className="cursor-pointer">
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        asChild
                                        title="Import config from another environment"
                                    >
                                        <span>
                                            <Upload className="h-4 w-4 mr-2" />
                                            Import
                                        </span>
                                    </Button>
                                    <input
                                        type="file"
                                        accept=".json"
                                        className="hidden"
                                        onChange={importOnedriveConfig}
                                    />
                                </label>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                💡 <strong>Tip:</strong> Use Export/Import to transfer OneDrive credentials between preview and production environments.
                            </p>
                        </div>
                    )}
                </div>

                <Separator />

                {/* LibreOffice Installation */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-base font-semibold flex items-center gap-2">
                                <Terminal className="h-4 w-4 text-violet-500" />
                                Presentation Converter (LibreOffice)
                            </Label>
                            <p className="text-sm text-muted-foreground">
                                Required for converting PowerPoint presentations to images for analysis
                            </p>
                        </div>
                    </div>
                    
                    <div className="p-4 rounded-xl border space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {libreOfficeStatus?.installed ? (
                                    <Badge className="bg-emerald-500">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Installed
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" className="text-amber-600 border-amber-500">
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        Not Installed
                                    </Badge>
                                )}
                                {libreOfficeStatus?.version && (
                                    <span className="text-sm text-muted-foreground">
                                        Version: {libreOfficeStatus.version}
                                    </span>
                                )}
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={checkLibreOfficeStatus}
                            >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Check Status
                            </Button>
                        </div>
                        
                        {!libreOfficeStatus?.installed && (
                            <div className="space-y-3">
                                <p className="text-sm text-amber-600 dark:text-amber-400">
                                    LibreOffice is required to convert PowerPoint presentations to images for AI analysis. 
                                    Without it, presentation uploads will fail.
                                </p>
                                <Button 
                                    onClick={installLibreOffice}
                                    disabled={installingLibreOffice}
                                    className="bg-violet-600 hover:bg-violet-700"
                                >
                                    {installingLibreOffice ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Installing... (this may take 2-5 minutes)
                                        </>
                                    ) : (
                                        <>
                                            <Terminal className="h-4 w-4 mr-2" />
                                            Install LibreOffice
                                        </>
                                    )}
                                </Button>
                                <p className="text-xs text-muted-foreground">
                                    ⚠️ <strong>Note:</strong> This installation is only needed once after deployment. 
                                    The installation runs in the background and may take a few minutes.
                                </p>
                            </div>
                        )}
                        
                        {libreOfficeStatus?.installed && (
                            <p className="text-sm text-emerald-600 dark:text-emerald-400">
                                ✓ LibreOffice is installed and ready. Presentation uploads should work correctly.
                            </p>
                        )}
                    </div>
                </div>

                <Separator />

                {/* FFmpeg Installation */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-base font-semibold flex items-center gap-2">
                                <Mic className="h-4 w-4 text-blue-500" />
                                Audio Processor (FFmpeg)
                            </Label>
                            <p className="text-sm text-muted-foreground">
                                Required for voice transcription in Role Play Lab, Evolve AI, and all voice features
                            </p>
                        </div>
                    </div>
                    
                    <div className="p-4 rounded-xl border space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {ffmpegStatus?.installed ? (
                                    <Badge className="bg-emerald-500">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Installed
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" className="text-red-600 border-red-500">
                                        <AlertTriangle className="h-3 w-3 mr-1" />
                                        Not Installed
                                    </Badge>
                                )}
                                {ffmpegStatus?.version && (
                                    <span className="text-sm text-muted-foreground truncate max-w-[300px]">
                                        {ffmpegStatus.version}
                                    </span>
                                )}
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={checkFfmpegStatus}
                            >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Check Status
                            </Button>
                        </div>
                        
                        {!ffmpegStatus?.installed && (
                            <div className="space-y-3">
                                <p className="text-sm text-red-600 dark:text-red-400">
                                    <strong>⚠️ FFmpeg is required for all voice features!</strong> Without it, voice recording 
                                    and transcription will not work in Role Play Lab, Evolve AI presentations, verbal quizzes, 
                                    and other voice-based training modules.
                                </p>
                                <Button 
                                    onClick={installFFmpeg}
                                    disabled={installingFFmpeg}
                                    className="bg-blue-600 hover:bg-blue-700"
                                >
                                    {installingFFmpeg ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Installing... (about 1 minute)
                                        </>
                                    ) : (
                                        <>
                                            <Mic className="h-4 w-4 mr-2" />
                                            Install FFmpeg
                                        </>
                                    )}
                                </Button>
                                <p className="text-xs text-muted-foreground">
                                    ⚠️ <strong>Note:</strong> This installation is required for voice features to work. 
                                    The installation runs in the background and takes about 1 minute.
                                </p>
                            </div>
                        )}
                        
                        {ffmpegStatus?.installed && (
                            <p className="text-sm text-emerald-600 dark:text-emerald-400">
                                ✓ FFmpeg is installed and ready. Voice transcription features are fully operational.
                            </p>
                        )}
                    </div>
                </div>

            </CardContent>
        </Card>
    );
};

export default EvolveSettings;
